import React, { useState } from 'react';
import { downloadStoredFile } from '../../services/fileStorageApi';
import type { MessageAttachmentRef } from '../../types';

const ICN_FILE = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

const ICN_DOWNLOAD = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const ICN_SPINNER = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" className="animate-spin">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

export interface MessageAttachmentChipProps extends MessageAttachmentRef {
  /** Visual variant: 'user' for outgoing bubble (inverted), 'agent' for incoming card. */
  variant?: 'user' | 'agent';
}

/**
 * Read-only file chip rendered inside a message bubble.
 * Clicking fetches the file as a blob via the BFF and triggers a browser download.
 */
export function MessageAttachmentChip({
  stored_file_ukey,
  file_name,
  content_type: _content_type,
  variant = 'agent',
}: MessageAttachmentChipProps): React.ReactElement {
  const [isDownloading, setIsDownloading] = useState(false);

  const label = file_name ?? stored_file_ukey;

  const handleClick = async (): Promise<void> => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadStoredFile(stored_file_ukey, label);
    } catch {
      // Download failure is non-fatal — surface nothing; user can retry by clicking again.
    } finally {
      setIsDownloading(false);
    }
  };

  const baseClass =
    'group inline-flex items-center gap-2 max-w-[220px] rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition-colors select-none';

  const variantClass =
    variant === 'user'
      ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
      : 'border-border-default bg-surface text-text-primary hover:bg-surface-hover';

  return (
    <button
      type="button"
      className={`${baseClass} ${variantClass}`}
      onClick={() => void handleClick()}
      disabled={isDownloading}
      aria-label={`Download attachment: ${label}`}
      title={label}
    >
      <span className={`shrink-0 ${variant === 'user' ? 'text-white/70' : 'text-text-tertiary'}`}>
        {ICN_FILE}
      </span>

      <span className="truncate font-medium" style={{ maxWidth: 140 }}>
        {label}
      </span>

      <span className={`shrink-0 ${variant === 'user' ? 'text-white/70' : 'text-text-tertiary'} opacity-0 group-hover:opacity-100 transition-opacity`}>
        {isDownloading ? ICN_SPINNER : ICN_DOWNLOAD}
      </span>
    </button>
  );
}
